import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useEffect, useRef, useState } from 'react';
import { useGetYTDValueByEmployeeId } from '@nxsys/hooks/api';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import AppLoader from '@nxsys/components/AppLoader';
import OneOffBenefitForm, {
  OneOffBenefitFormRef,
  OneOffBenefitFormValues,
} from './OneOffBenefitForm';

type CreateOneOffBenefitProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: OneOffBenefitFormValues) => Promise<void>;
  employeeId: string;
};

const CreateOneOffBenefitModal: React.FC<CreateOneOffBenefitProps> = ({
  isOpen,
  toggle,
  onSubmit,
  employeeId,
}: CreateOneOffBenefitProps) => {
  const { messages } = useTypedIntl();
  const currentTaxWeekTaxYear = getTaxWeekTaxYear();

  const formRef = useRef<OneOffBenefitFormRef>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: OneOffBenefitFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const {
    run: getYTDValueByEmployeeId,
    loading: isGetYTDValueByEmployeeIdLoading,
    value: ytdValueByEmployeeIdData,
  } = useGetYTDValueByEmployeeId();

  useEffect(() => {
    if (isOpen && employeeId) {
      getYTDValueByEmployeeId(employeeId, {
        taxYear: currentTaxWeekTaxYear.taxYear,
        taxWeek: currentTaxWeekTaxYear.taxWeek,
      });
    }
  }, [isOpen, employeeId]);

  useEffect(() => {
    if (ytdValueByEmployeeIdData) {
      formRef.current?.setFieldsValue({
        totalEeContribution:
          ytdValueByEmployeeIdData?.data?.oneOffTotalEeContribution?.toString() ||
          '0',
      });
    }
  }, [ytdValueByEmployeeIdData]);

  return (
    <AppModalContainer
      title={messages['common.addNewOneOffBenefit']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      {isGetYTDValueByEmployeeIdLoading && <AppLoader />}
      <OneOffBenefitForm formRef={formRef} onSubmit={handleSubmit} />
    </AppModalContainer>
  );
};

export default CreateOneOffBenefitModal;
